import { useEffect, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { money } from "@/lib/invoice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KIND = { statement: "statement", reminder: "payment reminder" };

export default function EmailStatementButton({ customer, kind = "statement", compact = false, testId }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/customer-email", { params: { customer: customer.name } })
      .then((r) => { setEmail(r.data.email); setLast(r.data.last_statement); }).catch((e) => toast.error(formatError(e)));
  }, [open, customer.name]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/statement/email", { customer: customer.name, email: email.trim(), kind });
      toast.success(`${kind === "reminder" ? "Reminder" : "Statement"} (${data.invoices} invoice${data.invoices === 1 ? "" : "s"}, ${money(data.total)}) emailed to ${data.to}`);
      setOpen(false);
    } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };

  return (
    <>
      {compact ? <button onClick={() => setOpen(true)} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={testId || `email-${kind}-button`}><Mail size={12} /> Email {kind === "reminder" ? "reminder" : "statement"}</button>
        : <Button variant="outline" onClick={() => setOpen(true)} data-testid={testId || "email-statement-button"} className="hover:bg-secondary hover:text-foreground"><Mail size={16} /> Email {KIND[kind]}</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-card" data-testid="email-statement-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Email {KIND[kind]}</DialogTitle>
            <DialogDescription>Sends {customer.name} a {KIND[kind]} using your wording from Admin panel → Invoices & email, with their statement of open invoices as a PDF. The address is saved on the customer for next time.</DialogDescription>
          </DialogHeader>
          <form onSubmit={send} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="stmt-email">Customer email</Label>
              <Input id="stmt-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="billing@customer.com" data-testid="email-statement-input" />
            </div>
            {last && <p className="text-xs text-muted-foreground" data-testid="email-statement-last">Last sent {new Date(last.at).toLocaleString()} to {last.to} ({money(last.total)})</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="email-statement-cancel">Cancel</Button>
              <Button type="submit" disabled={busy || !email.trim()} data-testid="email-statement-send">{busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Send</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
